import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import {
  ComponentBase,
  SharedService,
  BusinessDetailsService,
  ErrorFormComponent,
  POSITION,
  SavePhasesDataType,
} from 'src/app/core';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
  selector: 'app-add-employees',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FlexLayoutModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    ErrorFormComponent,
  ],
  templateUrl: './add-employees.component.html',
  styleUrl: './add-employees.component.scss',
})
export class AddEmployeesComponent extends ComponentBase implements OnInit {
  /** i18n keys for title/description/buttons so we can reuse this component. */
  @Input() titleKey = 'Add_Employees';
  @Input() descriptionKey: string | undefined = 'Add_employees_description_sidenav';
  @Input() submitLabelKey = 'Confirm';
  @Input() cancelLabelKey = 'Cancel';
  /** When false, hide internal action buttons (used when embedded in sidenav). */
  @Input() showActions = true;

  /** RegistrationCycleLogId to be used when saving. Falls back to cookies when not provided. */
  @Input() registrationCycleLogId: number | undefined;

  /** Emitted when save succeeds (used by smart-suggestions to close sidenav + reload). */
  @Output() confirm = new EventEmitter<void>();

  /** Emitted when user clicks Cancel. */
  @Output() cancel = new EventEmitter<void>();

  form!: FormGroup;
  positions: POSITION[] = [];
  employeeName = '';
  employeeList: string[] = [];
  dir = 'ltr';

  constructor(
    private readonly sharedService: SharedService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    readonly translateService: TranslateService
  ) {
    super();
    this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.getPositions();
    this.loadExistingData();
  }

  get canConfirm(): boolean {
    return !!this.form && this.form.valid && this.employeeList.length > 0 && !this.loading;
  }

  get employeeRole(): string {
    return this.positions.find(p => p.PositionId === this.form.get('PositionId')?.value)?.GlobalName || '';
  }

  private initForm(): void {
    const regIdRaw =
      this.registrationCycleLogId ??
      this.sharedService.getCookie('RegistrationCycleLogId');
    const regId =
      regIdRaw != null && regIdRaw !== ''
        ? typeof regIdRaw === 'number'
          ? regIdRaw
          : +regIdRaw
        : null;

    this.form = new UntypedFormGroup({
      RegistrationCycleLogId: new UntypedFormControl(regId),
      PositionId: new UntypedFormControl(null, Validators.required),
      SavePhasesDataType: new UntypedFormControl(SavePhasesDataType.Edit),
    });
  }

  private getPositions(): void {
    this.businessDetailsService
      .getPositions()
      .pipe(take(1))
      .subscribe((res) => {
        this.positions = res.Result ?? [];
      });
  }

  /** Load existing phase data (Phase Five) if present, to support editing. */
  private loadExistingData(): void {
    const regIdRaw =
      this.registrationCycleLogId ??
      this.sharedService.getCookie('RegistrationCycleLogId');
    if (regIdRaw == null || regIdRaw === '') return;
    const regId =
      typeof regIdRaw === 'number' ? regIdRaw : +regIdRaw;

    this.form.patchValue({ RegistrationCycleLogId: regId });
    this.businessDetailsService
      .getRegisteredPhases({ RegistrationCycleLogId: regId })
      .pipe(take(1))
      .subscribe((response) => {
        const five = response?.Result?.Five;
        if (five?.FiveData && (!five.DoneBySkip || five.Edited)) {
          const d = five.FiveData;
          this.form.patchValue({
            PositionId: d.PositionId,
          });
          this.employeeList = d.EmployeesNames ?? [];
          this.form
            .get('SavePhasesDataType')
            ?.setValue(SavePhasesDataType.Edit);
        }
      });
  }

  onAddEmployee(): void {
    const name = (this.employeeName ?? '').trim();
    if (!name) {
      this.toastr.warning(
        this.translateService.instant('ENTER_EMPLOYEE_NAME')
      );
      return;
    }
    if (this.employeeList.includes(name)) {
      this.toastr.warning('Employee Name Already Exist !');
      return;
    }
    this.employeeList.push(name);
    this.employeeName = '';
  }

  onRemovedEmployee(name: string): void {
    this.employeeList = this.employeeList.filter((emp) => emp !== name);
  }

  onCancelClick(): void {
    this.cancel.emit();
  }

  onSubmit(): void {
    if (!this.canConfirm) return;

    const regIdRaw = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regIdRaw) {
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }

    this.loading = true;
    const regId = typeof regIdRaw === 'number' ? regIdRaw : +regIdRaw;
    const payload = {
      ...this.form.value,
      RegistrationCycleLogId: regId,
      EmployeesNames: this.employeeList,
    };

    this.businessDetailsService
      .saveEmployeeAndBranch(payload)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.loading = false;
          const ok = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (ok) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.confirm.emit();
          }
        },
        () => {
          this.loading = false;
        }
      );
  }
}

